import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Typography,
  IconButton,
  TableSortLabel,
  Chip,
  CircularProgress,
  Alert,
  Pagination,
  Snackbar,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Tooltip,
  Skeleton,
} from "@mui/material";
import {
  Search,
  ArrowUpward,
  ArrowDownward,
  Refresh,
  Delete,
} from "@mui/icons-material";

const API_BASE_URL = `${process.env.REACT_APP_SUPERADMIN_APP_API_BASE_URL}`;

const ResultTable = ({ sidebarOpen = true }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [deleteLoading, setDeleteLoading] = useState({});
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    submissionId: null,
    submissionName: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch data from API
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/result/candidate-feedback`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        // Transform API data to match our table structure
        const transformedData = result.data.map((item) => ({
          _id: item._id,
          candidate_name: item.candidate_name,
          created_at: item.created_at,
          updated_at: item.updated_at,
          interview_ended_reason: item.interview_ended_reason,
          // Extract feedback data
          sentiment: item.candidate_feedback_for_interview?.sentiment || "N/A",
          rating: item.candidate_feedback_for_interview?.rating || 0,
          feedback:
            item.candidate_feedback_for_interview?.feedback ||
            "No feedback provided",
          submittedAt:
            item.candidate_feedback_for_interview?.submittedAt ||
            item.created_at,
        }));

        setData(transformedData);
        setPagination((prev) => ({
          ...prev,
          totalItems: transformedData.length,
        }));
      } else {
        throw new Error(result.message || "Failed to fetch data");
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(`Failed to load data: ${err.message}`);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;

    return data.filter(
      (item) =>
        item.candidate_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.feedback?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.interview_ended_reason
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        item.sentiment?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [data, searchTerm]);

  // Sort the filtered data FIRST (before pagination)
  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);

      if (sortOrder === "asc") {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });
  }, [filteredData, sortOrder]);

  // Then apply pagination to the sorted data
  const paginatedData = useMemo(() => {
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;

    const totalPages = Math.ceil(sortedData.length / pagination.itemsPerPage);

    // Update pagination state
    setPagination((prev) => ({
      ...prev,
      totalPages: totalPages,
      totalItems: sortedData.length,
    }));

    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, pagination.currentPage, pagination.itemsPerPage]);

  // Delete contact submission (simulated)
  const deleteContactSubmission = async (id) => {
    try {
      setDeleteLoading((prev) => ({ ...prev, [id]: true }));

      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Remove from local state
      setData((prevData) => prevData.filter((item) => item._id !== id));

      setSnackbar({
        open: true,
        message: "Candidate feedback deleted successfully",
        severity: "success",
      });

      // If current page becomes empty, go to previous page
      if (paginatedData.length === 1 && pagination.currentPage > 1) {
        setPagination((prev) => ({
          ...prev,
          currentPage: prev.currentPage - 1,
        }));
      }
    } catch (err) {
      console.error("Error deleting candidate feedback:", err);
      setSnackbar({
        open: true,
        message: `Delete failed: ${err.message}`,
        severity: "error",
      });
    } finally {
      setDeleteLoading((prev) => ({ ...prev, [id]: false }));
      setDeleteDialog({ open: false, submissionId: null, submissionName: "" });
    }
  };

  // Handle delete click
  const handleDeleteClick = (id, name) => {
    setDeleteDialog({
      open: true,
      submissionId: id,
      submissionName: name,
    });
  };

  // Handle delete confirm
  const handleDeleteConfirm = () => {
    if (deleteDialog.submissionId) {
      deleteContactSubmission(deleteDialog.submissionId);
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, submissionId: null, submissionName: "" });
  };

  // Handle page change
  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  };

  // Handle refresh
  const handleRefresh = async () => {
    await fetchData();
    setSnackbar({
      open: true,
      message: "Data refreshed successfully",
      severity: "success",
    });
  };

  const handleSortChange = () => {
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    // Reset to first page when sorting changes
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  // Modified date formatting function - shows only date without time
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return "success";
    if (rating >= 3) return "warning";
    return "error";
  };

  const getSentimentColor = (sentiment) => {
    const colors = {
      "Very Good": "success",
      Good: "success",
      Neutral: "warning",
      Bad: "error",
      "Very Bad": "error",
    };
    return colors[sentiment] || "default";
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Calculate sidebar width based on state
  const sidebarWidth = sidebarOpen ? 220 : 100;

  // Skeleton Row Component
  const SkeletonRow = () => (
    <TableRow
      sx={{
        "&:nth-of-type(even)": {
          bgcolor: "#f5f5f5",
        },
      }}
    >
      <TableCell sx={{ minWidth: 150 }}>
        <Skeleton variant="text" width="80%" height={24} />
      </TableCell>
      <TableCell sx={{ minWidth: 200 }}>
        <Skeleton variant="text" width="90%" height={24} />
      </TableCell>
      <TableCell sx={{ minWidth: 120 }}>
        <Skeleton variant="rounded" width={70} height={24} />
      </TableCell>
      <TableCell sx={{ minWidth: 100 }}>
        <Skeleton variant="rounded" width={30} height={24} />
      </TableCell>
      <TableCell sx={{ minWidth: 300 }}>
        <Skeleton variant="text" width="95%" height={20} />
        <Skeleton variant="text" width="85%" height={20} />
        <Skeleton variant="text" width="75%" height={20} />
      </TableCell>
      <TableCell sx={{ minWidth: 110 }}>
        <Skeleton variant="text" width="70%" height={24} />
      </TableCell>
      <TableCell sx={{ minWidth: 80, textAlign: "center" }}>
        <Skeleton
          variant="circular"
          width={40}
          height={40}
          sx={{ mx: "auto" }}
        />
      </TableCell>
    </TableRow>
  );

  return (
    <Box
      sx={{
        p: 4,
        backgroundColor: "#fafafa",
        minHeight: "100vh",
        ml: {
          xs: "0px",
          sm: "0px",
          md: `${sidebarWidth}px`,
          xl: `${sidebarWidth}px`,
        },
        transition: "margin-left 0.3s ease",
        fontFamily: "Outfit",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            color: "#9048FB",
            fontWeight: 600,
            fontFamily: "Outfit",
          }}
        >
          Feedback Provided by candidates for Interview conduction on Interview
          Panel
        </Typography>
        <IconButton
          onClick={handleRefresh}
          disabled={loading}
          sx={{
            bgcolor: "#9048FB",
            color: "white",
            "&:hover": { bgcolor: "#7c3aed" },
          }}
        >
          <Refresh />
        </IconButton>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Search and Sort Controls */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          alignItems: "center",
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <TextField
          variant="outlined"
          placeholder="Search by candidate name, feedback, or reason..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{
            flexGrow: 1,
            maxWidth: 400,
            backgroundColor: "white",
            "& .MuiInputBase-input": {
              fontFamily: "Outfit",
              fontSize: "16px",
            },
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontFamily: "Outfit" }}
          >
            Sort:
          </Typography>
          <IconButton
            onClick={handleSortChange}
            sx={{
              bgcolor: "#9048FB",
              color: "white",
              "&:hover": { bgcolor: "#7c3aed" },
            }}
          >
            {sortOrder === "desc" ? <ArrowDownward /> : <ArrowUpward />}
          </IconButton>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontFamily: "Outfit" }}
          >
            {sortOrder === "desc" ? "Newest" : "Oldest"}
          </Typography>
        </Box>
      </Box>

      {/* Data Table */}
      <Paper sx={{ overflow: "hidden", boxShadow: 2 }}>
        <TableContainer
          sx={{
            position: "relative",
            maxHeight: 700,
            overflow: "auto",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#a1a1a1",
            },
          }}
        >
          <Table sx={{ minWidth: 1000 }}>
            <TableHead sx={{ bgcolor: "#9048FB" }}>
              <TableRow>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 150,
                    whiteSpace: "nowrap",
                  }}
                >
                  Candidate Name
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 200,
                    whiteSpace: "nowrap",
                  }}
                >
                  Interview End Reason
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 120,
                    whiteSpace: "nowrap",
                  }}
                >
                  Sentiment
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 100,
                    whiteSpace: "nowrap",
                  }}
                >
                  Rating
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 300,
                    whiteSpace: "nowrap",
                  }}
                >
                  Feedback
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 110,
                    whiteSpace: "nowrap",
                  }}
                >
                  <TableSortLabel
                    active={true}
                    direction={sortOrder}
                    onClick={handleSortChange}
                    sx={{
                      color: "white !important",
                      fontFamily: "Outfit",
                      "& .MuiTableSortLabel-icon": {
                        color: "white !important",
                      },
                    }}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    fontFamily: "Outfit",
                    fontSize: "16px",
                    minWidth: 80,
                    whiteSpace: "nowrap",
                    textAlign: "center",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && data.length === 0 ? (
                // Show skeleton rows while loading
                Array.from({ length: 8 }, (_, index) => (
                  <SkeletonRow key={index} />
                ))
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    sx={{
                      textAlign: "center",
                      py: 4,
                      color: "text.secondary",
                      fontFamily: "Outfit",
                      fontSize: "16px",
                    }}
                  >
                    {searchTerm ? "No results found" : "No data available"}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row) => (
                  <TableRow
                    key={row._id}
                    sx={{
                      "&:nth-of-type(even)": {
                        bgcolor: "#f5f5f5",
                      },
                      "&:hover": {
                        bgcolor: "#f3f0ff",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontFamily: "Outfit",
                        fontSize: "16px",
                        minWidth: 150,
                      }}
                    >
                      {row.candidate_name || "N/A"}
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Outfit",
                          fontSize: "16px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.interview_ended_reason || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      <Chip
                        label={row.sentiment}
                        size="small"
                        color={getSentimentColor(row.sentiment)}
                        sx={{
                          fontFamily: "Outfit",
                          fontSize: "14px",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 100 }}>
                      <Chip
                        label={`${row.rating}`}
                        size="small"
                        color={getRatingColor(row.rating)}
                        sx={{
                          fontFamily: "Outfit",
                          fontSize: "14px",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 300, maxWidth: 400 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Outfit",
                          fontSize: "14px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          lineHeight: 1.4,
                        }}
                        title={row.feedback}
                      >
                        {row.feedback}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#A3AED0",
                        fontFamily: "Outfit",
                        fontSize: "16px",
                        minWidth: 110,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatDate(row.created_at)}
                    </TableCell>
                    <TableCell sx={{ minWidth: 80, textAlign: "center" }}>
                      <Tooltip title="Delete feedback">
                        <IconButton
                          onClick={() =>
                            handleDeleteClick(row._id, row.candidate_name)
                          }
                          disabled={deleteLoading[row._id]}
                          sx={{
                            color: "#f44336",
                            "&:hover": {
                              backgroundColor: "rgba(244, 67, 54, 0.1)",
                            },
                            "&:disabled": {
                              color: "rgba(244, 67, 54, 0.3)",
                            },
                          }}
                        >
                          {deleteLoading[row._id] ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <Delete />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pagination and Results Summary */}
      <Box
        sx={{
          mt: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontFamily: "Outfit", fontSize: "16px" }}
        >
          {loading && data.length === 0 ? (
            <Skeleton variant="text" width={200} height={24} />
          ) : (
            <>
              Showing {paginatedData.length} of {pagination.totalItems} entries
              {searchTerm && ` (filtered by "${searchTerm}")`}
            </>
          )}
        </Typography>

        {pagination.totalPages > 1 && !loading && (
          <Pagination
            count={pagination.totalPages}
            page={pagination.currentPage}
            onChange={handlePageChange}
            sx={{
              "& .MuiPaginationItem-root": {
                color: "#9048FB",
                fontFamily: "Outfit",
                fontSize: "16px",
                "&.Mui-selected": {
                  backgroundColor: "#9048FB",
                  color: "white",
                },
                "&:hover": {
                  backgroundColor: "#f3f0ff",
                },
              },
            }}
            disabled={loading}
          />
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle
          id="delete-dialog-title"
          sx={{
            fontFamily: "Outfit",
            fontWeight: 600,
            color: "#f44336",
          }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="delete-dialog-description"
            sx={{
              fontFamily: "Outfit",
              fontSize: "16px",
            }}
          >
            Are you sure you want to delete the feedback from{" "}
            <strong>{deleteDialog.submissionName}</strong>? This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleDeleteCancel}
            sx={{
              fontFamily: "Outfit",
              color: "#666",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              fontFamily: "Outfit",
              backgroundColor: "#f44336",
              "&:hover": {
                backgroundColor: "#d32f2f",
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            fontFamily: "Outfit",
            fontSize: "16px",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ResultTable;
